"use client";

import { c as _c } from "react/compiler-runtime";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useCallback, useMemo } from "react";
export type OptimisticRemainingPowerState = {
  readonly key: string;
  readonly pendingVotes: {
    readonly amount: number;
    readonly dropId: string;
  }[];
  readonly optimisticVoteCount: number;
  readonly settledRemainingPower: number | null;
  readonly summaryBaselineCount: number | null;
};
type SetOptimisticRemainingPowerState = React.Dispatch<React.SetStateAction<OptimisticRemainingPowerState>>;
type UseMemesQuickVoteOptimisticStateOptions = {
  readonly isSummarySuccess: boolean;
  readonly optimisticRemainingPowerKey: string;
  readonly optimisticRemainingPowerState: OptimisticRemainingPowerState;
  readonly summaryCount: number;
};
type UseMemesQuickVoteVoteHandlersOptions = {
  readonly isSummarySuccess: boolean;
  readonly optimisticRemainingPowerKey: string;
  readonly refetchSummary: () => Promise<unknown>;
  readonly removeDropId: (dropId: string) => void;
  readonly resyncDiscovery: () => void;
  readonly setAndPersistSkippedDropIds: (updater: (current: string[]) => string[]) => void;
  readonly setOptimisticRemainingPowerState: SetOptimisticRemainingPowerState;
  readonly summaryCount: number;
};
export const getOptimisticRemainingPowerKey = ({
  contextProfile,
  memesWaveId,
  sessionId
}: {
  readonly contextProfile: string | null | undefined;
  readonly memesWaveId: string | null | undefined;
  readonly sessionId: number;
}): string => `${sessionId}:${memesWaveId ?? ""}:${contextProfile ?? ""}`;
export const createInitialOptimisticRemainingPowerState = (key: string): OptimisticRemainingPowerState => ({
  key,
  pendingVotes: [],
  optimisticVoteCount: 0,
  settledRemainingPower: null,
  summaryBaselineCount: null
});
const getCurrentOptimisticRemainingPowerState = ({
  key,
  state
}: {
  readonly key: string;
  readonly state: OptimisticRemainingPowerState;
}): OptimisticRemainingPowerState => state.key === key ? state : createInitialOptimisticRemainingPowerState(key);
const deriveSummaryOptimismState = ({
  hasPendingVotes,
  isSummarySuccess,
  optimisticVoteCount,
  summaryBaselineCount,
  summaryCount
}: {
  readonly hasPendingVotes: boolean;
  readonly isSummarySuccess: boolean;
  readonly optimisticVoteCount: number;
  readonly summaryBaselineCount: number | null;
  readonly summaryCount: number;
}) => {
  if (summaryBaselineCount === null || optimisticVoteCount === 0) {
    return {
      isSettled: true,
      unreflectedVoteCount: 0
    };
  }
  const reflectedVoteCount = isSummarySuccess ? Math.max(0, summaryBaselineCount - summaryCount) : 0;
  const isSettled = !hasPendingVotes && isSummarySuccess && reflectedVoteCount >= optimisticVoteCount;
  return {
    isSettled,
    unreflectedVoteCount: isSettled ? 0 : Math.max(0, optimisticVoteCount - reflectedVoteCount)
  };
};
export const useMemesQuickVoteOptimisticState = t0 => {
  const $ = _c(13);
  const {
    isSummarySuccess,
    optimisticRemainingPowerKey,
    optimisticRemainingPowerState,
    summaryCount
  } = t0;
  let t1;
  if ($[0] !== optimisticRemainingPowerKey || $[1] !== optimisticRemainingPowerState) {
    t1 = getCurrentOptimisticRemainingPowerState({
      key: optimisticRemainingPowerKey,
      state: optimisticRemainingPowerState
    });
    $[0] = optimisticRemainingPowerKey;
    $[1] = optimisticRemainingPowerState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currentOptimisticRemainingPowerState = t1;
  const hasPendingOptimisticVotes = currentOptimisticRemainingPowerState.pendingVotes.length > 0;
  let t2;
  bb0: {
    const pendingSpent = currentOptimisticRemainingPowerState.pendingVotes.reduce(_temp, 0);
    const settledRemainingPower = currentOptimisticRemainingPowerState.settledRemainingPower;
    if (settledRemainingPower === null) {
      t2 = null;
      break bb0;
    }
    t2 = Math.max(0, settledRemainingPower - pendingSpent);
  }
  const optimisticRemainingPower = t2;
  let t3;
  if ($[3] !== currentOptimisticRemainingPowerState.optimisticVoteCount || $[4] !== currentOptimisticRemainingPowerState.summaryBaselineCount || $[5] !== hasPendingOptimisticVotes || $[6] !== isSummarySuccess || $[7] !== summaryCount) {
    t3 = deriveSummaryOptimismState({
      hasPendingVotes: hasPendingOptimisticVotes,
      isSummarySuccess,
      optimisticVoteCount: currentOptimisticRemainingPowerState.optimisticVoteCount,
      summaryBaselineCount: currentOptimisticRemainingPowerState.summaryBaselineCount,
      summaryCount
    });
    $[3] = currentOptimisticRemainingPowerState.optimisticVoteCount;
    $[4] = currentOptimisticRemainingPowerState.summaryBaselineCount;
    $[5] = hasPendingOptimisticVotes;
    $[6] = isSummarySuccess;
    $[7] = summaryCount;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const summaryOptimismState = t3;
  let t4;
  if ($[9] !== hasPendingOptimisticVotes || $[10] !== optimisticRemainingPower || $[11] !== summaryOptimismState) {
    t4 = {
      hasPendingOptimisticVotes,
      optimisticRemainingPower,
      summaryOptimismState
    };
    $[9] = hasPendingOptimisticVotes;
    $[10] = optimisticRemainingPower;
    $[11] = summaryOptimismState;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
export const useMemesQuickVoteVoteHandlers = t0 => {
  const $ = _c(23);
  const {
    isSummarySuccess,
    optimisticRemainingPowerKey,
    refetchSummary,
    removeDropId,
    resyncDiscovery,
    setAndPersistSkippedDropIds,
    setOptimisticRemainingPowerState,
    summaryCount
  } = t0;
  let t1;
  if ($[0] !== refetchSummary || $[1] !== removeDropId || $[2] !== setAndPersistSkippedDropIds) {
    t1 = dropId => {
      removeDropId(dropId);
      setAndPersistSkippedDropIds(current => current.filter(value => value !== dropId));
      refetchSummary();
    };
    $[0] = refetchSummary;
    $[1] = removeDropId;
    $[2] = setAndPersistSkippedDropIds;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleInvalidatedDrop = t1;
  let t2;
  if ($[4] !== isSummarySuccess || $[5] !== optimisticRemainingPowerKey || $[6] !== removeDropId || $[7] !== setOptimisticRemainingPowerState || $[8] !== summaryCount) {
    t2 = (drop, amount) => {
      removeDropId(drop.id);
      setOptimisticRemainingPowerState(current_0 => {
        const baseState = getCurrentOptimisticRemainingPowerState({
          key: optimisticRemainingPowerKey,
          state: current_0
        });
        const reflectedVoteCount = baseState.summaryBaselineCount === null || !isSummarySuccess ? 0 : Math.max(0, baseState.summaryBaselineCount - summaryCount);
        const isSummaryCycleSettled = baseState.summaryBaselineCount !== null && baseState.optimisticVoteCount > 0 && baseState.pendingVotes.length === 0 && isSummarySuccess && reflectedVoteCount >= baseState.optimisticVoteCount;
        const shouldResetSummaryCycle = baseState.summaryBaselineCount === null || baseState.optimisticVoteCount === 0 || isSummaryCycleSettled;
        return {
          ...baseState,
          pendingVotes: [...baseState.pendingVotes, {
            amount,
            dropId: drop.id
          }],
          settledRemainingPower: baseState.pendingVotes.length === 0 ? drop.context_profile_context?.max_rating ?? null : baseState.settledRemainingPower ?? drop.context_profile_context?.max_rating ?? null,
          optimisticVoteCount: shouldResetSummaryCycle || !isSummarySuccess ? 1 : baseState.optimisticVoteCount + 1,
          summaryBaselineCount: isSummarySuccess && shouldResetSummaryCycle ? summaryCount : baseState.summaryBaselineCount
        };
      });
    };
    $[4] = isSummarySuccess;
    $[5] = optimisticRemainingPowerKey;
    $[6] = removeDropId;
    $[7] = setOptimisticRemainingPowerState;
    $[8] = summaryCount;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleVoteQueued = t2;
  let t3;
  if ($[10] !== optimisticRemainingPowerKey || $[11] !== setOptimisticRemainingPowerState) {
    t3 = (_drop, _amount, nextRemainingPower) => {
      setOptimisticRemainingPowerState(current_1 => {
        const baseState_0 = getCurrentOptimisticRemainingPowerState({
          key: optimisticRemainingPowerKey,
          state: current_1
        });
        const currentVote = baseState_0.pendingVotes[0];
        if (!currentVote) {
          return {
            ...baseState_0,
            settledRemainingPower: nextRemainingPower
          };
        }
        return {
          ...baseState_0,
          pendingVotes: baseState_0.pendingVotes.slice(1),
          settledRemainingPower: nextRemainingPower
        };
      });
    };
    $[10] = optimisticRemainingPowerKey;
    $[11] = setOptimisticRemainingPowerState;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleVoteSuccess = t3;
  let t4;
  if ($[13] !== optimisticRemainingPowerKey || $[14] !== refetchSummary || $[15] !== resyncDiscovery || $[16] !== setOptimisticRemainingPowerState) {
    t4 = (_drop_0, _amount_0) => {
      setOptimisticRemainingPowerState(current_2 => {
        const baseState_1 = getCurrentOptimisticRemainingPowerState({
          key: optimisticRemainingPowerKey,
          state: current_2
        });
        if (baseState_1.pendingVotes.length === 0) {
          return baseState_1;
        }
        const nextPendingVotes = baseState_1.pendingVotes.slice(1);
        const nextOptimisticVoteCount = Math.max(0, baseState_1.optimisticVoteCount - 1);
        return {
          ...baseState_1,
          pendingVotes: nextPendingVotes,
          optimisticVoteCount: nextOptimisticVoteCount,
          settledRemainingPower: nextPendingVotes.length === 0 ? null : baseState_1.settledRemainingPower,
          summaryBaselineCount: nextOptimisticVoteCount === 0 ? null : baseState_1.summaryBaselineCount
        };
      });
      resyncDiscovery();
      refetchSummary();
    };
    $[13] = optimisticRemainingPowerKey;
    $[14] = refetchSummary;
    $[15] = resyncDiscovery;
    $[16] = setOptimisticRemainingPowerState;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleVoteFailure = t4;
  let t5;
  if ($[18] !== handleInvalidatedDrop || $[19] !== handleVoteFailure || $[20] !== handleVoteQueued || $[21] !== handleVoteSuccess) {
    t5 = {
      handleInvalidatedDrop,
      handleVoteFailure,
      handleVoteQueued,
      handleVoteSuccess
    };
    $[18] = handleInvalidatedDrop;
    $[19] = handleVoteFailure;
    $[20] = handleVoteQueued;
    $[21] = handleVoteSuccess;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
};
function _temp(sum, vote) {
  return sum + vote.amount;
}